/**
 * useAutoSave Hook
 * ORCA360 - Auto-save with debouncing
 *
 * Automatically saves data after a delay when changes are detected
 * Prevents data loss and reduces manual save operations
 *
 * Sprint 4, Day 4
 */

import { useEffect, useRef, useState, useCallback } from 'react';

export interface UseAutoSaveOptions<T> {
  /**
   * The data to auto-save
   */
  data: T;

  /**
   * Function to call when saving
   * Should return a promise that resolves when save is complete
   */
  onSave: (data: T) => Promise<void>;

  /**
   * Delay in milliseconds before auto-saving after changes stop
   * Default: 3000ms (3 seconds)
   */
  delay?: number;

  /**
   * Whether auto-save is enabled
   * Default: true
   */
  enabled?: boolean;

  /**
   * Callback when save starts
   */
  onSaveStart?: () => void;

  /**
   * Callback when save completes successfully
   */
  onSaveSuccess?: () => void;

  /**
   * Callback when save fails
   */
  onSaveError?: (error: Error) => void;
}

export interface UseAutoSaveReturn {
  /**
   * Whether data is currently being saved
   */
  isSaving: boolean;

  /**
   * Whether there are unsaved changes
   */
  hasUnsavedChanges: boolean;

  /**
   * Error from last save attempt
   */
  error: Error | null;

  /**
   * Manually trigger a save
   */
  save: () => Promise<void>;

  /**
   * Reset the unsaved changes flag
   */
  reset: () => void;

  /**
   * Timestamp of last successful save
   */
  lastSaved: Date | null;
}

/**
 * Auto-save hook with debouncing
 */
export function useAutoSave<T>({
  data,
  onSave,
  delay = 3000,
  enabled = true,
  onSaveStart,
  onSaveSuccess,
  onSaveError,
}: UseAutoSaveOptions<T>): UseAutoSaveReturn {
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const previousDataRef = useRef<T>(data);
  const isMountedRef = useRef(true);

  /**
   * Save function
   */
  const save = useCallback(async () => {
    if (!enabled || isSaving) {
      return;
    }

    try {
      setIsSaving(true);
      setError(null);

      if (onSaveStart) {
        onSaveStart();
      }

      await onSave(data);

      if (isMountedRef.current) {
        setHasUnsavedChanges(false);
        setLastSaved(new Date());
        previousDataRef.current = data;

        if (onSaveSuccess) {
          onSaveSuccess();
        }
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to save');

      if (isMountedRef.current) {
        setError(error);

        if (onSaveError) {
          onSaveError(error);
        }
      }
    } finally {
      if (isMountedRef.current) {
        setIsSaving(false);
      }
    }
  }, [data, enabled, isSaving, onSave, onSaveStart, onSaveSuccess, onSaveError]);

  /**
   * Reset unsaved changes flag
   */
  const reset = useCallback(() => {
    setHasUnsavedChanges(false);
    setError(null);
    previousDataRef.current = data;
  }, [data]);

  /**
   * Auto-save effect
   */
  useEffect(() => {
    if (!enabled) {
      return;
    }

    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Check if data has changed
    const dataChanged = JSON.stringify(data) !== JSON.stringify(previousDataRef.current);

    if (dataChanged) {
      setHasUnsavedChanges(true);

      // Set new timeout for auto-save
      timeoutRef.current = setTimeout(() => {
        save();
      }, delay);
    }

    // Cleanup
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [data, delay, enabled, save]);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      isMountedRef.current = false;

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  /**
   * Warn on page unload if there are unsaved changes
   */
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  return {
    isSaving,
    hasUnsavedChanges,
    error,
    save,
    reset,
    lastSaved,
  };
}
